import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Plus,
  MoreHorizontal,
  Pencil,
  Trash2,
  Loader2,
  Building,
  MessageSquare,
  Link2,
  CheckCircle,
  Users,
  Star,
  ToggleLeft,
  ToggleRight,
  Instagram,
  Mail,
  Globe,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { StatusDot } from '@/components/tenant/SettingsInfoRow';
import { supabase } from '@/integrations/supabase/client';
import { useAutoOpenFromQuery } from '@/hooks/useAutoOpenFromQuery';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { getAvatarColorClasses } from '@/lib/utils/avatar-colors';
import { CreateDepartmentDialog } from '@/components/tenant/departments/CreateDepartmentDialog';
import { EditDepartmentDialog } from '@/components/tenant/departments/EditDepartmentDialog';
import { LinkInboxesDialog } from '@/components/tenant/departments/LinkInboxesDialog';
import { LinkAgentsDialog } from '@/components/tenant/departments/LinkAgentsDialog';

// Mapeia channel_type da inbox para um ícone visual
const getChannelIcon = (channelType?: string) => {
  const t = (channelType || '').toLowerCase();
  if (t.includes('instagram')) return Instagram;
  if (t.includes('mail') || t.includes('email')) return Mail;
  if (t.includes('web') || t.includes('chat')) return Globe;
  return MessageSquare;
};

// ============================================
// Interfaces para tipagem dos dados
// ============================================

// Interface para vínculo departamento-inbox
interface DepartmentInbox {
  inbox_id: string;
  is_primary: boolean;
  inbox: {
    id: string;
    name: string;
    channel_type: string;
    identifier: string;
  };
}

// Interface para vínculo departamento-agente
interface DepartmentAgent {
  user_id: string;
  is_supervisor: boolean;
  profile: {
    id: string;
    full_name: string | null;
    email: string;
    avatar_url: string | null;
  };
}

// Interface para departamento com suas inboxes, agentes e configurações de distribuição
interface Department {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  created_at: string;
  inboxes: DepartmentInbox[];
  agents: DepartmentAgent[];
  // Campos de distribuição automática
  auto_distribution_enabled?: boolean;
  distribution_method?: 'round_robin' | 'least_busy';
  max_conversations_per_agent?: number;
  // v2.0.0: Configuração de supervisores na distribuição
  supervisor_receives_distribution?: boolean;
  // v3.0.0: Fila de distribuição global
  default_queue_id?: string | null;
}

// Interface para inbox disponível para vínculo
interface Inbox {
  id: string;
  name: string;
  channel_type: string;
  identifier: string;
}

// ============================================
// Componente de Card de Métrica
// Padrão visual idêntico ao DealListKpiCards
// ============================================
interface MetricCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  variant: 'primary' | 'success' | 'info' | 'purple';
}

/**
 * Card de métrica compacto - mesmo layout do DealListKpiCards
 * Ícone+título à esquerda, valor grande à direita
 */
const MetricCard = ({ title, value, icon, variant }: MetricCardProps) => {
  /** Mapeamento de variantes para cores (padrão DealListKpiCards) */
  const variantStyles = {
    primary: {
      cardBg: 'bg-purple-100 dark:bg-purple-900/30',
      iconColor: 'text-purple-700 dark:text-purple-300',
    },
    success: {
      cardBg: 'bg-lime-100 dark:bg-green-900/30',
      iconColor: 'text-green-700 dark:text-green-300',
    },
    info: {
      cardBg: 'bg-blue-100 dark:bg-blue-900/30',
      iconColor: 'text-blue-700 dark:text-blue-300',
    },
    purple: {
      cardBg: 'bg-cyan-100 dark:bg-cyan-900/30',
      iconColor: 'text-cyan-700 dark:text-cyan-300',
    },
  };

  const styles = variantStyles[variant];

  return (
    <Card className={cn('border-0 shadow-none cursor-default transition-all hover:shadow-md', styles.cardBg)}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between gap-2">
          {/* Ícone + título */}
          <div className="flex items-center gap-2 min-w-0">
            <div className={`shrink-0 ${styles.iconColor}`}>{icon}</div>
            <p className="text-xs font-medium truncate text-gray-900 dark:text-gray-100">
              {title}
            </p>
          </div>
          {/* Valor */}
          <p className="text-xl font-bold leading-tight shrink-0 text-gray-900 dark:text-gray-100">
            {value}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

// ============================================
// Componente Principal
// ============================================

/**
 * Componente de gerenciamento de departamentos do tenant
 * Utiliza permissões granulares para controle de acesso:
 * - canCreate('departments'): criar departamentos
 * - canEdit('departments'): editar, ativar/desativar e vincular inboxes
 * - canDelete('departments'): excluir departamentos
 */
const TenantDepartments = () => {
  const { currentTenant } = useAuth();
  const { canCreate, canEdit, canDelete } = usePermissions();
  const [departments, setDepartments] = useState<Department[]>([]);
  const [inboxes, setInboxes] = useState<Inbox[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Dialogs state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  // Abre modal via ?new=1 (atalho "Novo departamento" do Quick Create do header).
  useAutoOpenFromQuery('1', () => setCreateDialogOpen(true));
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [linkInboxesOpen, setLinkInboxesOpen] = useState(false);
  const [linkAgentsOpen, setLinkAgentsOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  const [selectedDepartment, setSelectedDepartment] = useState<Department | null>(null);

  // ============================================
  // Cálculo de Métricas
  // ============================================
  const totalDepartments = departments.length;
  const activeDepartments = departments.filter(d => d.is_active).length;
  const withInboxes = departments.filter(d => d.inboxes.length > 0).length;
  const withAgents = departments.filter(d => d.agents.length > 0).length;

  useEffect(() => {
    if (currentTenant?.id) {
      fetchDepartments();
      fetchInboxes();
    }
  }, [currentTenant?.id]);

  // ============================================
  // Funções de Fetch de Dados
  // ============================================

  // Server-side: usa RPC get_tenant_departments_full (retorna JSONB pré-agregado)
  const fetchDepartments = async () => {
    if (!currentTenant?.id) return;
    try {
      setLoading(true);
      const { data, error } = await supabase.rpc('get_tenant_departments_full', {
        p_tenant_hint: currentTenant.id,
      });
      if (error) throw error;
      const list = (data as unknown as Department[]) || [];
      // Ordena por nome (a RPC já ordena, mas garante consistência)
      list.sort((a, b) => a.name.localeCompare(b.name));
      setDepartments(list);
    } catch (error) {
      console.error('Error fetching departments:', error);
      toast.error('Erro ao carregar departamentos');
    } finally {
      setLoading(false);
    }
  };

  // Server-side: reusa RPC existente get_tenant_inboxes_safe
  const fetchInboxes = async () => {
    if (!currentTenant?.id) return;
    try {
      const { data, error } = await supabase.rpc('get_tenant_inboxes_safe', {
        p_tenant_hint: currentTenant.id,
      });
      if (error) throw error;
      setInboxes(
        (data || []).map((i: any) => ({
          id: i.id,
          name: i.name,
          channel_type: i.channel_type,
          identifier: i.identifier,
        })),
      );
    } catch (error) {
      console.error('Error fetching inboxes:', error);
    }
  };

  // ============================================
  // Handlers de Ações
  // ============================================

  const handleEdit = (department: Department) => {
    setSelectedDepartment(department);
    setEditDialogOpen(true);
  };

  const handleDelete = (department: Department) => {
    setSelectedDepartment(department);
    setDeleteDialogOpen(true);
  };

  const handleLinkInboxes = (department: Department) => {
    setSelectedDepartment(department);
    setLinkInboxesOpen(true);
  };

  const handleLinkAgents = (department: Department) => {
    setSelectedDepartment(department);
    setLinkAgentsOpen(true);
  };

  // Server-side: RPC toggle_department_active
  const handleToggleActive = async (department: Department) => {
    try {
      const { error } = await supabase.rpc('toggle_department_active', {
        p_department_id: department.id,
        p_tenant_hint: currentTenant?.id,
      });
      if (error) throw error;
      toast.success(`Departamento ${department.is_active ? 'desativado' : 'ativado'}`);
      fetchDepartments();
    } catch (error) {
      console.error('Error toggling department:', error);
      toast.error('Erro ao alterar status do departamento');
    }
  };

  // Server-side: RPC delete_department
  const confirmDelete = async () => {
    if (!selectedDepartment) return;
    try {
      const { error } = await supabase.rpc('delete_department', {
        p_department_id: selectedDepartment.id,
        p_tenant_hint: currentTenant?.id,
      });
      if (error) throw error;
      toast.success('Departamento excluído com sucesso');
      fetchDepartments();
    } catch (error) {
      console.error('Error deleting department:', error);
      toast.error('Erro ao excluir departamento');
    } finally {
      setDeleteDialogOpen(false);
      setSelectedDepartment(null);
    }
  };

  // ============================================
  // Loading State
  // ============================================
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // ============================================
  // Render Principal
  // ============================================
  return (
    <div className="flex flex-col h-full">
      {/* Header compacto - padrão Contatos */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b">
        <div>
          <h1 className="text-xl font-semibold">Departamentos</h1>
          <p className="text-xs text-muted-foreground">
            Organize sua equipe em departamentos e vincule caixas de entrada
          </p>
        </div>

        {/* Botão Novo Departamento - permissão de criar */}
        <div className="flex flex-wrap gap-2">
          {canCreate('departments') && (
            <Button 
              size="xs"
              onClick={() => setCreateDialogOpen(true)}
            >
              <Plus className="mr-1.5" />
              Novo Departamento
            </Button>
          )}
        </div>
      </div>

      {/* Cards de Métricas */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 px-4 py-3">
        <MetricCard
          title="Total Departamentos"
          value={totalDepartments}
          icon={<Building className="h-5 w-5" />}
          variant="primary"
        />
        <MetricCard
          title="Ativos"
          value={activeDepartments}
          icon={<CheckCircle className="h-5 w-5" />}
          variant="success"
        />
        <MetricCard
          title="Com Inbox Vinculadas"
          value={withInboxes}
          icon={<Link2 className="h-5 w-5" />}
          variant="info"
        />
        <MetricCard
          title="Com Agentes Vinculados"
          value={withAgents}
          icon={<Users className="h-5 w-5" />}
          variant="purple"
        />
      </div>

      {/* Tabela de Departamentos — visual SaaS premium, sem gradientes/cards pretos */}
      <div className="rounded-xl border bg-card overflow-hidden">
        {departments.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
            <div className="rounded-full bg-muted p-5 mb-4">
              <Building className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-base font-semibold text-foreground mb-1">
              Nenhum departamento cadastrado
            </h3>
            <p className="text-sm text-muted-foreground mb-5 max-w-md">
              Crie departamentos para organizar sua equipe e rotear conversas automaticamente.
            </p>
            {canCreate('departments') && (
              <Button size="xs" onClick={() => setCreateDialogOpen(true)}>
                <Plus className="mr-1.5" />
                Criar Primeiro Departamento
              </Button>
            )}
          </div>
        ) : (
          <TooltipProvider delayDuration={150}>
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Departamento</TableHead>
                  <TableHead>Descrição</TableHead>
                  <TableHead>Inbox Vinculadas</TableHead>
                  <TableHead>Agentes Vinculados</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {departments.map((department) => (
                  <TableRow key={department.id} className="hover:bg-muted/20">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="rounded-lg bg-muted p-2">
                          <Building className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <span className="font-medium text-foreground">{department.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[200px]">
                      <span className="text-muted-foreground truncate block">
                        {department.description || '—'}
                      </span>
                    </TableCell>

                    {/* Inbox Vinculadas — chips neutros estilo pill */}
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-1.5">
                        {department.inboxes.length > 0 ? (
                          <>
                            {department.inboxes.slice(0, 2).map(link => {
                              const ChannelIcon = getChannelIcon(link.inbox.channel_type);
                              return (
                                <span
                                  key={link.inbox_id}
                                  className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-muted/40 px-2 py-0.5 text-xs text-foreground"
                                >
                                  <ChannelIcon className="h-3 w-3 text-muted-foreground" />
                                  <span className="truncate max-w-[140px]">{link.inbox.name}</span>
                                  {link.is_primary && (
                                    <Star className="h-3 w-3 text-amber-500 fill-amber-500" />
                                  )}
                                </span>
                              );
                            })}
                            {department.inboxes.length > 2 && (
                              <span className="inline-flex items-center rounded-full border border-border/60 bg-muted/40 px-2 py-0.5 text-xs text-muted-foreground">
                                +{department.inboxes.length - 2}
                              </span>
                            )}
                          </>
                        ) : (
                          <span className="text-sm text-muted-foreground">Nenhuma vinculada</span>
                        )}
                      </div>
                    </TableCell>

                    {/* Agentes Vinculados — avatar stack */}
                    <TableCell>
                      {department.agents.length > 0 ? (
                        <div className="flex items-center">
                          <div className="flex -space-x-2">
                            {department.agents.slice(0, 4).map(agent => {
                              const displayName = agent.profile.full_name || agent.profile.email;
                              const initials = displayName?.substring(0, 2).toUpperCase();
                              return (
                                <Tooltip key={agent.user_id}>
                                  <TooltipTrigger asChild>
                                    <div className="relative">
                                      <Avatar className="h-7 w-7 ring-2 ring-background">
                                        <AvatarImage src={agent.profile.avatar_url || undefined} />
                                        <AvatarFallback
                                          className={cn(
                                            'text-[10px]',
                                            getAvatarColorClasses(displayName),
                                          )}
                                        >
                                          {initials}
                                        </AvatarFallback>
                                      </Avatar>
                                      {agent.is_supervisor && (
                                        <Star className="absolute -top-0.5 -right-0.5 h-3 w-3 text-amber-500 fill-amber-500" />
                                      )}
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    {displayName}
                                    {agent.is_supervisor && ' • Supervisor'}
                                  </TooltipContent>
                                </Tooltip>
                              );
                            })}
                          </div>
                          {department.agents.length > 4 && (
                            <span className="ml-2 inline-flex items-center rounded-full border border-border/60 bg-muted/40 px-2 py-0.5 text-xs text-muted-foreground">
                              +{department.agents.length - 4}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">Nenhum vinculado</span>
                      )}
                    </TableCell>

                    {/* Status — Badge outline + StatusDot (padrão Perfis de Permissão) */}
                    <TableCell>
                      <Badge
                        variant="outline"
                        className="h-5 gap-1.5 px-1.5 text-[10px] font-medium uppercase tracking-wider"
                      >
                        <StatusDot tone={department.is_active ? 'emerald' : 'muted'} />
                        {department.is_active ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </TableCell>

                    {/* Ações — toggle ativar/desativar como icon-button + dropdown */}
                    <TableCell>
                      <div className="flex items-center justify-end gap-0.5">
                        {canEdit('departments') && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handleToggleActive(department)}
                              >
                                {department.is_active ? (
                                  <ToggleRight className="h-4 w-4 text-emerald-600" />
                                ) : (
                                  <ToggleLeft className="h-4 w-4 text-muted-foreground" />
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              {department.is_active ? 'Desativar' : 'Ativar'}
                            </TooltipContent>
                          </Tooltip>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {canEdit('departments') && (
                              <DropdownMenuItem onClick={() => handleEdit(department)}>
                                <Pencil className="h-4 w-4 mr-2" />
                                Editar
                              </DropdownMenuItem>
                            )}
                            {canEdit('departments') && (
                              <DropdownMenuItem onClick={() => handleLinkInboxes(department)}>
                                <Link2 className="h-4 w-4 mr-2" />
                                Vincular Inbox
                              </DropdownMenuItem>
                            )}
                            {canEdit('departments') && (
                              <DropdownMenuItem onClick={() => handleLinkAgents(department)}>
                                <Users className="h-4 w-4 mr-2" />
                                Vincular Agentes
                              </DropdownMenuItem>
                            )}
                            {canDelete('departments') && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => handleDelete(department)}
                                  className="text-destructive focus:text-destructive"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Excluir
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TooltipProvider>
        )}
      </div>

      {/* Dialogs - Sempre montados para preservar estado */}
      <CreateDepartmentDialog
        open={createDialogOpen && !!currentTenant}
        onOpenChange={setCreateDialogOpen}
        tenantId={currentTenant?.id ?? ''}
        onSuccess={fetchDepartments}
      />

      <EditDepartmentDialog
        open={editDialogOpen && !!selectedDepartment}
        onOpenChange={setEditDialogOpen}
        department={selectedDepartment}
        onSuccess={fetchDepartments}
      />

      <LinkInboxesDialog
        open={linkInboxesOpen && !!selectedDepartment}
        onOpenChange={setLinkInboxesOpen}
        department={selectedDepartment}
        availableInboxes={inboxes}
        onSuccess={fetchDepartments}
      />

      <LinkAgentsDialog
        open={linkAgentsOpen && !!selectedDepartment}
        onOpenChange={setLinkAgentsOpen}
        department={selectedDepartment}
        tenantId={currentTenant?.id ?? ''}
        onSuccess={fetchDepartments}
      />

      {/* Confirmação de Exclusão */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir departamento?</AlertDialogTitle>
            <AlertDialogDescription>
              Essa ação não pode ser desfeita. Isso irá excluir o departamento 
              "{selectedDepartment?.name}" e desvincular todas as inboxes associadas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TenantDepartments;
